import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Package, MessageSquare, Mail, CreditCard, Plus, Pencil, Trash2, 
  Eye, EyeOff, LogOut, Check, X, ArrowLeft, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getProducts, createProduct, updateProduct, deleteProduct,
  getSubscribers, getChatMessages, markMessageRead, getTransactions,
  verifyAdminKey
} from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';

const AdminPage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminKey, setAdminKey] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [isProductDialogOpen, setIsProductDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    price: '',
    original_price: '',
    image_url: '',
    category: 'storage',
    features: '',
    in_stock: true,
    is_featured: false,
    is_bundle: false,
    bundle_items: '',
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!adminKey.trim()) {
      toast.error('Please enter admin key');
      return;
    }

    setIsAuthenticating(true);
    try {
      await verifyAdminKey(adminKey);
      setIsAuthenticated(true);
      localStorage.setItem('nestflow_admin_key', adminKey);
      toast.success('Welcome to Admin Dashboard');
    } catch (error) {
      toast.error('Invalid admin key');
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setAdminKey('');
    localStorage.removeItem('nestflow_admin_key');
    toast.success('Logged out successfully');
  };

  useEffect(() => {
    const savedKey = localStorage.getItem('nestflow_admin_key');
    if (savedKey) {
      setAdminKey(savedKey);
      verifyAdminKey(savedKey)
        .then(() => setIsAuthenticated(true))
        .catch(() => localStorage.removeItem('nestflow_admin_key'));
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const [productsData, subscribersData, messagesData, transactionsData] = await Promise.all([
          getProducts(),
          getSubscribers(adminKey),
          getChatMessages(adminKey),
          getTransactions(adminKey),
        ]);
        setProducts(productsData);
        setSubscribers(subscribersData);
        setMessages(messagesData);
        setTransactions(transactionsData);
      } catch (error) {
        console.error('Failed to fetch data:', error);
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isAuthenticated, adminKey]);

  const resetProductForm = () => {
    setProductForm({
      name: '',
      description: '',
      price: '',
      original_price: '',
      image_url: '',
      category: 'storage',
      features: '',
      in_stock: true,
      is_featured: false,
      is_bundle: false,
      bundle_items: '',
    });
    setEditingProduct(null);
  };

  const handleOpenProductDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setProductForm({
        name: product.name,
        description: product.description,
        price: product.price.toString(),
        original_price: product.original_price?.toString() || '',
        image_url: product.image_url,
        category: product.category,
        features: product.features.join('\n'),
        in_stock: product.in_stock,
        is_featured: product.is_featured,
        is_bundle: product.is_bundle,
        bundle_items: product.bundle_items.join('\n'),
      });
    } else {
      resetProductForm();
    }
    setIsProductDialogOpen(true);
  };

  const handleSaveProduct = async () => {
    if (!productForm.name || !productForm.price || !productForm.image_url) {
      toast.error('Please fill in required fields');
      return;
    }

    const productData = {
      name: productForm.name,
      description: productForm.description,
      price: parseFloat(productForm.price),
      original_price: productForm.original_price ? parseFloat(productForm.original_price) : null,
      image_url: productForm.image_url,
      category: productForm.category,
      features: productForm.features.split('\n').filter(f => f.trim()),
      in_stock: productForm.in_stock,
      is_featured: productForm.is_featured,
      is_bundle: productForm.is_bundle,
      bundle_items: productForm.bundle_items.split('\n').filter(b => b.trim()),
    };

    try {
      if (editingProduct) {
        await updateProduct(editingProduct.id, productData, adminKey);
        toast.success('Product updated');
      } else {
        await createProduct(productData, adminKey);
        toast.success('Product created');
      }
      
      const updated = await getProducts();
      setProducts(updated);
      setIsProductDialogOpen(false);
      resetProductForm();
    } catch (error) {
      toast.error('Failed to save product');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await deleteProduct(productId, adminKey);
      setProducts(products.filter(p => p.id !== productId));
      toast.success('Product deleted');
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleMarkAsRead = async (messageId) => {
    try {
      await markMessageRead(messageId, adminKey);
      setMessages(messages.map(m => m.id === messageId ? { ...m, is_read: true } : m));
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-6" data-testid="admin-login-page">
        <div className="w-full max-w-md">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-[#5C5C60] hover:text-[#1A1A1C] mb-8 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            Back to Store
          </button>

          <div className="bg-white rounded-2xl p-8 border border-[#F3F2EE] shadow-sm">
            <h1 className="text-2xl font-bold text-[#1A1A1C] font-['Manrope'] mb-2">
              Admin Login
            </h1>
            <p className="text-[#5C5C60] mb-6">
              Enter your admin key to access the dashboard
            </p>

            <form onSubmit={handleLogin} className="space-y-4">
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Admin Key"
                  value={adminKey}
                  onChange={(e) => setAdminKey(e.target.value)}
                  className="pr-10 bg-[#F3F2EE] border-0 rounded-xl"
                  data-testid="admin-key-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A8A8E] hover:text-[#5C5C60]"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" strokeWidth={1.5} />
                  ) : (
                    <Eye className="w-5 h-5" strokeWidth={1.5} />
                  )}
                </button>
              </div>

              <Button
                type="submit"
                disabled={isAuthenticating}
                className="w-full bg-[#1A1A1C] text-white rounded-full py-6 font-medium hover:bg-[#333333]"
                data-testid="admin-login-btn"
              >
                {isAuthenticating ? 'Verifying...' : 'Login to Dashboard'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E5E5] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-[#5C5C60] hover:text-[#1A1A1C] transition-colors"
            >
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <h1 className="text-xl font-bold text-[#1A1A1C] font-['Manrope']">
              NestFlow Admin
            </h1>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-[#5C5C60] hover:text-[#FF3B30] transition-colors"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-5 h-5" strokeWidth={1.5} />
            Logout
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#F3F2EE] p-1 rounded-xl mb-8">
            <TabsTrigger 
              value="products" 
              className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"
              data-testid="tab-products"
            >
              <Package className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Products ({products.length})
            </TabsTrigger>
            <TabsTrigger 
              value="messages" 
              className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"
              data-testid="tab-messages"
            >
              <MessageSquare className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Messages ({messages.filter(m => !m.is_read).length})
            </TabsTrigger>
            <TabsTrigger 
              value="subscribers" 
              className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"
              data-testid="tab-subscribers"
            >
              <Mail className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Subscribers ({subscribers.length})
            </TabsTrigger>
            <TabsTrigger 
              value="transactions" 
              className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"
              data-testid="tab-transactions"
            >
              <CreditCard className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Transactions ({transactions.length})
            </TabsTrigger>
          </TabsList>

          {/* Products Tab */}
          <TabsContent value="products">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-[#1A1A1C] font-['Manrope']">
                Products
              </h2>
              <Button
                onClick={() => handleOpenProductDialog()}
                className="bg-[#1A1A1C] text-white rounded-full hover:bg-[#333333]"
                data-testid="add-product-btn"
              >
                <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Add Product
              </Button>
            </div>

            {loading ? (
              <div className="text-center py-12 text-[#5C5C60]">Loading...</div>
            ) : (
              <div className="bg-white rounded-2xl border border-[#F3F2EE] overflow-hidden">
                <table className="w-full">
                  <thead className="bg-[#F3F2EE]">
                    <tr>
                      <th className="text-left px-6 py-4 text-sm font-medium text-[#5C5C60]">Product</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-[#5C5C60]">Category</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-[#5C5C60]">Price</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-[#5C5C60]">Status</th>
                      <th className="text-right px-6 py-4 text-sm font-medium text-[#5C5C60]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product) => (
                      <tr key={product.id} className="border-t border-[#F3F2EE]" data-testid={`product-row-${product.id}`}>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <img
                              src={product.image_url}
                              alt={product.name}
                              className="w-12 h-12 rounded-lg object-cover bg-[#F3F2EE]"
                            />
                            <div>
                              <p className="font-medium text-[#1A1A1C]">{product.name}</p>
                              {product.is_bundle && (
                                <span className="text-xs text-[#34C759]">Bundle</span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-[#5C5C60] capitalize">{product.category}</td>
                        <td className="px-6 py-4">
                          <span className="font-medium text-[#1A1A1C]">${product.price.toFixed(2)}</span>
                          {product.original_price && (
                            <span className="text-sm text-[#8A8A8E] line-through ml-2">
                              ${product.original_price.toFixed(2)}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            product.in_stock 
                              ? 'bg-[#34C759]/10 text-[#34C759]' 
                              : 'bg-[#FF3B30]/10 text-[#FF3B30]'
                          }`}>
                            {product.in_stock ? 'In Stock' : 'Out of Stock'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleOpenProductDialog(product)}
                              className="p-2 text-[#5C5C60] hover:text-[#1A1A1C] hover:bg-[#F3F2EE] rounded-lg transition-colors"
                              data-testid={`edit-product-${product.id}`}
                            >
                              <Pencil className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                            <button
                              onClick={() => handleDeleteProduct(product.id)}
                              className="p-2 text-[#5C5C60] hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded-lg transition-colors"
                              data-testid={`delete-product-${product.id}`}
                            >
                              <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <h2 className="text-2xl font-bold text-[#1A1A1C] font-['Manrope'] mb-6">
              Chat Messages
            </h2>

            {messages.length === 0 ? (
              <div className="text-center py-12 text-[#5C5C60] bg-white rounded-2xl border border-[#F3F2EE]">
                No messages yet
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`bg-white rounded-2xl p-6 border ${
                      message.is_read ? 'border-[#F3F2EE]' : 'border-[#1A1A1C]'
                    }`}
                    data-testid={`message-${message.id}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <p className="font-medium text-[#1A1A1C]">{message.name}</p>
                        <p className="text-sm text-[#5C5C60]">{message.email}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-[#8A8A8E]">
                          {new Date(message.created_at).toLocaleDateString()}
                        </span>
                        {!message.is_read && (
                          <button
                            onClick={() => handleMarkAsRead(message.id)}
                            className="p-1 text-[#34C759] hover:bg-[#34C759]/10 rounded"
                            title="Mark as read"
                          >
                            <Check className="w-4 h-4" strokeWidth={2} />
                          </button>
                        )}
                      </div>
                    </div>
                    <p className="text-[#5C5C60]">{message.message}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Subscribers Tab */}
          <TabsContent value="subscribers">
            <h2 className="text-2xl font-bold text-[#1A1A1C] font-['Manrope'] mb-6">
              Newsletter Subscribers
            </h2>

            {subscribers.length === 0 ? (
              <div className="text-center py-12 text-[#5C5C60] bg-white rounded-2xl border border-[#F3F2EE]">
                No subscribers yet
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-[#F3F2EE] overflow-hidden">
                <table className="w-full">
                  <thead className="bg-[#F3F2EE]">
                    <tr>
                      <th className="text-left px-6 py-4 text-sm font-medium text-[#5C5C60]">Email</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-[#5C5C60]">Subscribed Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscribers.map((sub) => (
                      <tr key={sub.id} className="border-t border-[#F3F2EE]">
                        <td className="px-6 py-4 text-[#1A1A1C]">{sub.email}</td>
                        <td className="px-6 py-4 text-[#5C5C60]">
                          {new Date(sub.subscribed_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions">
            <h2 className="text-2xl font-bold text-[#1A1A1C] font-['Manrope'] mb-6">
              Payment Transactions
            </h2>

            {transactions.length === 0 ? (
              <div className="text-center py-12 text-[#5C5C60] bg-white rounded-2xl border border-[#F3F2EE]">
                No transactions yet
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-[#F3F2EE] overflow-hidden">
                <table className="w-full">
                  <thead className="bg-[#F3F2EE]">
                    <tr>
                      <th className="text-left px-6 py-4 text-sm font-medium text-[#5C5C60]">Order #</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-[#5C5C60]">Amount</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-[#5C5C60]">Status</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-[#5C5C60]">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((tx) => (
                      <tr key={tx.id} className="border-t border-[#F3F2EE]" data-testid={`transaction-${tx.id}`}>
                        <td className="px-6 py-4 font-mono text-sm font-semibold text-[#1A1A1C]">
                          {tx.order_number || tx.session_id.slice(0, 20) + '...'}
                        </td>
                        <td className="px-6 py-4 font-medium text-[#1A1A1C]">
                          ${tx.amount.toFixed(2)} {tx.currency.toUpperCase()}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            tx.payment_status === 'paid' 
                              ? 'bg-[#34C759]/10 text-[#34C759]' 
                              : tx.payment_status === 'initiated' || tx.payment_status === 'pending'
                              ? 'bg-[#FFA500]/10 text-[#FFA500]'
                              : 'bg-[#FF3B30]/10 text-[#FF3B30]'
                          }`}>
                            {tx.payment_status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-[#5C5C60]">
                          {new Date(tx.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Product Dialog */}
      <Dialog open={isProductDialogOpen} onOpenChange={setIsProductDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold font-['Manrope']">
              {editingProduct ? 'Edit Product' : 'Add New Product'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-[#5C5C60] mb-1 block">Name *</label>
                <Input
                  value={productForm.name}
                  onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                  placeholder="Product name"
                  className="rounded-xl"
                  data-testid="product-name-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[#5C5C60] mb-1 block">Category</label>
                <Select
                  value={productForm.category}
                  onValueChange={(v) => setProductForm({ ...productForm, category: v })}
                >
                  <SelectTrigger className="rounded-xl" data-testid="product-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kitchen">Kitchen</SelectItem>
                    <SelectItem value="storage">Storage</SelectItem>
                    <SelectItem value="drawers">Drawers</SelectItem>
                    <SelectItem value="tech">Tech</SelectItem>
                    <SelectItem value="bundles">Bundles</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-[#5C5C60] mb-1 block">Description</label>
              <Textarea
                value={productForm.description}
                onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                placeholder="Product description"
                rows={3}
                className="rounded-xl"
                data-testid="product-description-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-[#5C5C60] mb-1 block">Price *</label>
                <Input
                  type="number"
                  step="0.01"
                  value={productForm.price}
                  onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                  placeholder="29.99"
                  className="rounded-xl"
                  data-testid="product-price-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[#5C5C60] mb-1 block">Original Price</label>
                <Input
                  type="number"
                  step="0.01"
                  value={productForm.original_price}
                  onChange={(e) => setProductForm({ ...productForm, original_price: e.target.value })}
                  placeholder="39.99 (for discount)"
                  className="rounded-xl"
                  data-testid="product-original-price-input"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-[#5C5C60] mb-1 block">Image URL *</label>
              <Input
                value={productForm.image_url}
                onChange={(e) => setProductForm({ ...productForm, image_url: e.target.value })}
                placeholder="https://..."
                className="rounded-xl"
                data-testid="product-image-input"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-[#5C5C60] mb-1 block">Features (one per line)</label>
              <Textarea
                value={productForm.features}
                onChange={(e) => setProductForm({ ...productForm, features: e.target.value })}
                placeholder="Feature 1&#10;Feature 2&#10;Feature 3"
                rows={3}
                className="rounded-xl"
                data-testid="product-features-input"
              />
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  checked={productForm.in_stock}
                  onCheckedChange={(c) => setProductForm({ ...productForm, in_stock: c })}
                  data-testid="product-instock-switch"
                />
                <label className="text-sm text-[#5C5C60]">In Stock</label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={productForm.is_featured}
                  onCheckedChange={(c) => setProductForm({ ...productForm, is_featured: c })}
                  data-testid="product-featured-switch"
                />
                <label className="text-sm text-[#5C5C60]">Featured</label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={productForm.is_bundle}
                  onCheckedChange={(c) => setProductForm({ ...productForm, is_bundle: c })}
                  data-testid="product-bundle-switch"
                />
                <label className="text-sm text-[#5C5C60]">Bundle</label>
              </div>
            </div>

            {productForm.is_bundle && (
              <div>
                <label className="text-sm font-medium text-[#5C5C60] mb-1 block">Bundle Items (one per line)</label>
                <Textarea
                  value={productForm.bundle_items}
                  onChange={(e) => setProductForm({ ...productForm, bundle_items: e.target.value })}
                  placeholder="Item 1&#10;Item 2&#10;Item 3"
                  rows={3}
                  className="rounded-xl"
                  data-testid="product-bundle-items-input"
                />
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setIsProductDialogOpen(false)}
                className="rounded-full"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveProduct}
                className="bg-[#1A1A1C] text-white rounded-full hover:bg-[#333333]"
                data-testid="save-product-btn"
              >
                {editingProduct ? 'Save Changes' : 'Create Product'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
